import {
    bodyComposition, IBodyAssessment,
    NutritionRating, ScanStat, StatMonth, WorkoutStat,
} from 'fitbeat.models';
import moment from 'moment';
import {
    BodyShapeMap, ShouldersWiderThanHipsMap, WeightPatternMap, WristEncircleMap,
} from '../../config/bodycompositionconfig';
import { getWorkoutDuration, homeWorkoutDurationConfig } from '../../config/homeworkoutdurations';
import { StatRangesConfig } from '../../config/statrangesconfig';
import { UserProfile } from '../../entities';
import { UserWorkoutSession } from '../../entities/workoutsession';

// Utils
export const groupBy = (items: any, key: any) => items.reduce(
    (result: any, item: any) => ({
        ...result,
        [item[key]]: [
            ...(result[item[key]] || []),
            item,
        ],
    }),
    {});

export const takeFirstOnGroup = (group: any) => {
    const keys = Object.keys(group);
    return keys.map((key) => {
        const value = group[key];
        return value[0];
    });
};

export const getDaysInMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

export const round = (val: number, factor = 10) => {
    return Math.round(val * factor) / factor;
};

export const roundWhole = (val: number) => {
    return Math.round(val);
};

export const calcChangeDiff = (curr: number, change: number = 0) => {
    if (!curr || curr === 0 || !change) { return; }
    return Math.round((curr - change) * 10) / 10;
};

export const changeDescription = (changeVal: number | undefined, change: 'month' | 'scan') => {
    if (!changeVal || changeVal === 0) { return; }
    return `${changeVal < 0 ? 'down' : 'up'} on last ${change}`;
};

// Data specific utils
export const getMinMaxGraphDataPoints = (graphDataPoints: any) => {
    if (!graphDataPoints) { return; }
    const yPoints = graphDataPoints
        .filter((points: { x: number, y: number }) => points.y)
        .map((points: { x: number, y: number }) => points.y);
    let min = Math.min(...yPoints);
    min -= 1;
    const max = Math.max(...yPoints);

    return { min, max, ticks: 5 };
};

export const getAnalysisValue = (mostRecentScanStatsForSixMonths: any,
                                 statMonth: StatMonth,
                                 scanStat: ScanStat) => {
    let currentAnalysisValue: number | undefined;
    if (mostRecentScanStatsForSixMonths &&
        mostRecentScanStatsForSixMonths[statMonth] &&
        mostRecentScanStatsForSixMonths[statMonth].analysis &&
        mostRecentScanStatsForSixMonths[statMonth].analysis[scanStat]) {
        currentAnalysisValue = mostRecentScanStatsForSixMonths[statMonth].analysis[scanStat].currentValue;
    }
    return currentAnalysisValue;
};

export const calcMuscleMassMeter = (userProfile: UserProfile, weight: number = 0, bodyFat: number = 0) => {
    if (bodyFat === 0) { return 0; }
    const { organMass, boneMass } = StatRangesConfig.getOrganAndBoneMass(userProfile.gender, weight);
    const bodyFatAbsolute = weight * (bodyFat / 100);
    const muscleMassAbsolute = weight - bodyFatAbsolute - organMass - boneMass;
    const muscleMassMeter = muscleMassAbsolute / (userProfile.height / 100);
    return muscleMassMeter;
};

export const calcMuscleMassMeterKg = (userProfile: UserProfile, muscleMassMeter: number) => {
    return muscleMassMeter * (userProfile.height / 100);
};

export const calculatebodyFatPercentageFromBodyFatMass = (bodyFatMass: number, weight: number) => {
    return (bodyFatMass / weight) * 100;
};

export const calculatebodyFatMassFromBodyFatPercentage = (bodyFatPercentage: number, weight: number) => {
    return weight * (bodyFatPercentage / 100);
};

export const groupedSequenceFromRatingsForRequestedMonth = (ratingsForRequestedMonth: NutritionRating[]) => {
    return ratingsForRequestedMonth
        .filter((x) => x.percent !== null)
        .reduce((result: any[], item, i, arr) => {
            if ((arr[i - 1] && arr[i - 1].percent) !== item.percent) {
                result.push([]);
            }
            result[result.length - 1].push(item);
            return result;
        }, []);
};

// Graph data
export const getWorkoutStatsGraphDataPoints = (workoutStatsForSixMonths: any, statType: WorkoutStat) => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const dataPoints = [
        {
            x: monthNames[workoutStatsForSixMonths.monthSix.month - 1],
            y: workoutStatsForSixMonths.monthSix[statType] ?
                round((workoutStatsForSixMonths.monthSix[statType] /
                    workoutStatsForSixMonths.monthSix.totalMinutes) * 60) : undefined,
        },
        {
            x: monthNames[workoutStatsForSixMonths.monthFive.month - 1],
            y: workoutStatsForSixMonths.monthFive[statType] ?
                round((workoutStatsForSixMonths.monthFive[statType] /
                    workoutStatsForSixMonths.monthFive.totalMinutes) * 60) : undefined,
        },
        {
            x: monthNames[workoutStatsForSixMonths.monthFour.month - 1],
            y: workoutStatsForSixMonths.monthFour[statType] ?
                round((workoutStatsForSixMonths.monthFour[statType] /
                    workoutStatsForSixMonths.monthFour.totalMinutes) * 60) : undefined,
        },
        {
            x: monthNames[workoutStatsForSixMonths.monthThree.month - 1],
            y: workoutStatsForSixMonths.monthThree[statType] ?
                round((workoutStatsForSixMonths.monthThree[statType] /
                    workoutStatsForSixMonths.monthThree.totalMinutes) * 60) : undefined,
        },
        {
            x: monthNames[workoutStatsForSixMonths.previousMonth.month - 1],
            y: workoutStatsForSixMonths.previousMonth[statType] ?
                round((workoutStatsForSixMonths.previousMonth[statType] /
                    workoutStatsForSixMonths.previousMonth.totalMinutes) * 60) : undefined,
        },
        {
            x: monthNames[workoutStatsForSixMonths.selectedMonth.month - 1],
            y: workoutStatsForSixMonths.selectedMonth[statType] ?
                round((workoutStatsForSixMonths.selectedMonth[statType] /
                    workoutStatsForSixMonths.selectedMonth.totalMinutes) * 60) : undefined,
        },
    ];

    return dataPoints.every((x) => x.y === undefined) ? undefined : dataPoints;
};

export const getMuscleMassMeterGraphDataPoints = (
    mostRecentScanStatsForSixMonths: any, userProfile: UserProfile | undefined) => {
    if (!mostRecentScanStatsForSixMonths || !userProfile) { return; }
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return [
        {
            x: monthNames[mostRecentScanStatsForSixMonths.monthSix.month - 1],
            y: getMuscleMassMeterDataPoint(userProfile, mostRecentScanStatsForSixMonths, StatMonth.MonthSix),
        },
        {
            x: monthNames[mostRecentScanStatsForSixMonths.monthFive.month - 1],
            y: getMuscleMassMeterDataPoint(userProfile, mostRecentScanStatsForSixMonths, StatMonth.MonthFive),
        },
        {
            x: monthNames[mostRecentScanStatsForSixMonths.monthFour.month - 1],
            y: getMuscleMassMeterDataPoint(userProfile, mostRecentScanStatsForSixMonths, StatMonth.MonthFour),
        },
        {
            x: monthNames[mostRecentScanStatsForSixMonths.monthThree.month - 1],
            y: getMuscleMassMeterDataPoint(userProfile, mostRecentScanStatsForSixMonths, StatMonth.MonthThree),
        },
        {
            x: monthNames[mostRecentScanStatsForSixMonths.previousMonth.month - 1],
            y: getMuscleMassMeterDataPoint(userProfile, mostRecentScanStatsForSixMonths, StatMonth.PreviousMonth),
        },
        {
            x: monthNames[mostRecentScanStatsForSixMonths.selectedMonth.month - 1],
            y: getMuscleMassMeterDataPoint(userProfile, mostRecentScanStatsForSixMonths, StatMonth.SelectedMonth),
        }];
};

export const getUserScanStatsGraphDataPoints = (mostRecentScanStatsForSixMonths: any, statType: ScanStat) => {
    if (!mostRecentScanStatsForSixMonths) { return; }
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return [
        {
            x: monthNames[mostRecentScanStatsForSixMonths.monthSix.month - 1],
            y: mostRecentScanStatsForSixMonths.monthSix.analysis ?
                round(mostRecentScanStatsForSixMonths.monthSix.analysis[statType].currentValue) : undefined,
        },
        {
            x: monthNames[mostRecentScanStatsForSixMonths.monthFive.month - 1],
            y: mostRecentScanStatsForSixMonths.monthFive.analysis ?
                round(mostRecentScanStatsForSixMonths.monthFive.analysis[statType].currentValue) : undefined,
        },
        {
            x: monthNames[mostRecentScanStatsForSixMonths.monthFour.month - 1],
            y: mostRecentScanStatsForSixMonths.monthFour.analysis ?
                round(mostRecentScanStatsForSixMonths.monthFour.analysis[statType].currentValue) : undefined,
        },
        {
            x: monthNames[mostRecentScanStatsForSixMonths.monthThree.month - 1],
            y: mostRecentScanStatsForSixMonths.monthThree.analysis ?
                round(mostRecentScanStatsForSixMonths.monthThree.analysis[statType].currentValue) : undefined,
        },
        {
            x: monthNames[mostRecentScanStatsForSixMonths.previousMonth.month - 1],
            y: mostRecentScanStatsForSixMonths.previousMonth.analysis ?
                round(mostRecentScanStatsForSixMonths.previousMonth.analysis[statType].currentValue) : undefined,
        },
        {
            x: monthNames[mostRecentScanStatsForSixMonths.selectedMonth.month - 1],
            y: mostRecentScanStatsForSixMonths.selectedMonth.analysis ?
                round(mostRecentScanStatsForSixMonths.selectedMonth.analysis[statType].currentValue) : undefined,
        },
    ];
};

export const getNutritionRatingsGraphDataPoints = (mostRecentNutritionRatingsForSixMonths: any) => {
    if (!mostRecentNutritionRatingsForSixMonths) { return; }
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return [
        {
            x: monthNames[mostRecentNutritionRatingsForSixMonths.monthSix.month - 1],
            y: mostRecentNutritionRatingsForSixMonths.monthSix.rating ?
                mostRecentNutritionRatingsForSixMonths.monthSix.rating : undefined,
        },
        {
            x: monthNames[mostRecentNutritionRatingsForSixMonths.monthFive.month - 1],
            y: mostRecentNutritionRatingsForSixMonths.monthFive.rating ?
                mostRecentNutritionRatingsForSixMonths.monthFive.rating : undefined,
        },
        {
            x: monthNames[mostRecentNutritionRatingsForSixMonths.monthFour.month - 1],
            y: mostRecentNutritionRatingsForSixMonths.monthFour.rating ?
                mostRecentNutritionRatingsForSixMonths.monthFour.rating : undefined,
        },
        {
            x: monthNames[mostRecentNutritionRatingsForSixMonths.monthThree.month - 1],
            y: mostRecentNutritionRatingsForSixMonths.monthThree.rating ?
                mostRecentNutritionRatingsForSixMonths.monthThree.rating : undefined,
        },
        {
            x: monthNames[mostRecentNutritionRatingsForSixMonths.previousMonth.month - 1],
            y: mostRecentNutritionRatingsForSixMonths.previousMonth.rating ?
                mostRecentNutritionRatingsForSixMonths.previousMonth.rating : undefined,
        },
        {
            x: monthNames[mostRecentNutritionRatingsForSixMonths.selectedMonth.month - 1],
            y: mostRecentNutritionRatingsForSixMonths.selectedMonth.rating ?
                mostRecentNutritionRatingsForSixMonths.selectedMonth.rating : undefined,
        },
    ];
};

export const getSomatotypeBasedOnAnswers = (answers: IBodyAssessment): bodyComposition => {
    const q1 = WeightPatternMap.get(answers.weightPattern);
    if (!q1) { return 0; }
    const q2 = ShouldersWiderThanHipsMap.get(answers.shouldersWiderThanHips);
    if (!q2) { return 0; }
    const q3 = BodyShapeMap.get(answers.bodyShape);
    if (!q3) { return 0; }
    const q4 = WristEncircleMap.get(answers.wristEncircle);
    if (!q4) { return 0; }

    const totals = {
        [bodyComposition.meso]:
            q1[bodyComposition.meso] + q2[bodyComposition.meso] +
            q3[bodyComposition.meso] + q4[bodyComposition.meso],
        [bodyComposition.endo_meso]:
            q1[bodyComposition.endo_meso] + q2[bodyComposition.endo_meso] +
            q3[bodyComposition.endo_meso] + q4[bodyComposition.endo_meso],
        [bodyComposition.ecto_meso]:
            q1[bodyComposition.ecto_meso] + q2[bodyComposition.ecto_meso] +
            q3[bodyComposition.ecto_meso] + q4[bodyComposition.ecto_meso],
        [bodyComposition.endo]:
            q1[bodyComposition.endo] + q2[bodyComposition.endo] +
            q3[bodyComposition.endo] + q4[bodyComposition.endo],
        [bodyComposition.ecto]:
            q1[bodyComposition.ecto] + q2[bodyComposition.ecto] +
            q3[bodyComposition.ecto] + q4[bodyComposition.ecto],
    };

    const biggest = Object.keys(totals).sort((a, b) => totals[b] - totals[a])[0];
    return bodyComposition[biggest];
};

// ErrorHandlers
export const bodyscansErrorHandler = (mostRecentScanStatsForSixMonths: any) => {
    if (!mostRecentScanStatsForSixMonths) {
        return 'Requires a body scan';
    } else if (!mostRecentScanStatsForSixMonths.selectedMonth.analysis) {
        const month = getMonthName(mostRecentScanStatsForSixMonths.selectedMonth.month);
        return `Body scan for ${month} is due`;
    }
    return;
};

export const scanStatsErrorHandler = (scanStatsForMonth: any) => {
    if (!scanStatsForMonth) {
        return 'Requires a body scan';
    }
    return;
};

export const nutritionRatingsErrorHandler = (
    nutritionRatingTimeline: any, lastNutritionRating: NutritionRating | undefined, dueDay: Date) => {
    if (!nutritionRatingTimeline) {
        return 'Nutrition rating is due';
    }
    if (lastNutritionRating && moment(lastNutritionRating.date).isBefore(moment(dueDay))) {
        return 'Nutrition rating is due';
    }
    return;
};

export const heartRateMonitorErrorHandler = (userProfile: UserProfile | undefined, workoutStatsForSixMonths: any) => {
    if (!userProfile || !userProfile.heartRateMonitorIdentifier || userProfile.heartRateMonitorIdentifier === '0') {
        return 'Heart rate monitor setup is required';
    } else if (!workoutStatsForSixMonths.selectedMonth.avWorkoutEffort) {
        return 'Complete a workout with your heart rate monitor to track your performance.';
    }

    return;
};

const getTotalMSInWorkout = (workout: UserWorkoutSession) => {
  const startTimeOfFirstExerciseInMs = new Date(workout.startTimeOfFirstExercise).getTime();
  const endTimeOfWorkoutInMs = new Date(workout.endTime).getTime();
  const additionalSecondsBeforeFirstExerciseInMs = ((workout.additionalSecondsBeforeFirstExercise || 0) * 1000);
  return endTimeOfWorkoutInMs - startTimeOfFirstExerciseInMs - additionalSecondsBeforeFirstExerciseInMs;
};

export const getTotalSecondsInWorkout = (workout: UserWorkoutSession) => {
    return Math.round(getTotalMSInWorkout(workout) / 1000);
}

export const calcTotalMinutes = (workout: any) => {
  return Math.round(getTotalMSInWorkout(workout) / 60000);
};

export const getTotalMinutesCeil = (workoutType: number, totalMinutes: number) => {
    const maximumAllowedMinutes = homeWorkoutDurationConfig[workoutType]
        ? getWorkoutDuration(workoutType).workoutDurationInMinutes
        : Number.MAX_SAFE_INTEGER;
    const minutesForWorkout = Math.min(
        Math.floor(maximumAllowedMinutes),
        Math.ceil(totalMinutes),
    );
    return minutesForWorkout;
};

// no need to export
const getMonthName = (month: number) => {
    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December']
        ;
    return monthNames[month - 1];
};

const getMuscleMassMeterDataPoint = (
    userProfile: UserProfile, mostRecentScanStatsForSixMonths: any, statMonth: StatMonth) => {
    const currentWeight =
        getAnalysisValue(mostRecentScanStatsForSixMonths, statMonth, ScanStat.Weight);
    const currentPercentBodyFat =
        getAnalysisValue(mostRecentScanStatsForSixMonths, statMonth, ScanStat.PercentBodyFat);

    if (!currentWeight || !currentPercentBodyFat) {
        return undefined;
    }

    const muscleMassMeter = calcMuscleMassMeter(userProfile, currentWeight, currentPercentBodyFat);
    const result = calcMuscleMassMeterKg(userProfile, muscleMassMeter);
    return round(result);
};

export const sumDurationOfWorkoutInMinutes = (workouts: UserWorkoutSession[]): number => {
    return workouts.reduce((totalMinutes: number, workout) => {
        const totalMinutesInWorkout = calcTotalMinutes(workout);
        const minutesForWorkout = getTotalMinutesCeil(workout.workoutType, totalMinutesInWorkout);
        return totalMinutes + minutesForWorkout;
    }, 0);
};

export const getWorkoutsBeforeDate = (workouts: UserWorkoutSession[], date: Date): UserWorkoutSession[] => {
    return workouts.filter((workout) => new Date(workout.startTime).getTime() <= date.getTime());
};
